import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getMainAxios } from '../utils/util';
import {
  page, card, titleRow, gridTitle, gridHint, toolbarLabel,
  btnPrimary, selectSm, emptyMsg, listRow, listRowHeader, listRowDetail, colors,
} from '../styles/fdsTheme';

/**
 * Agent ▸ LogViewer — main.applogtsdb 조회 (읽기 전용).
 *
 * 적재는 fdsloopsvc(FDSAI.Loop logsvc)가 한다.
 * 설계: docs/design/AILoop-설계-구현계획.md §3-1·§4-2
 */

interface LogRow {
  t: string;
  app: string;
  host: string;
  lvl: number;
  level: string;
  msg: string;
  tmpl?: string | null;
  ex?: string | null;
  src?: string | null;
  kind?: string;
}

interface SearchRes {
  from: string;
  to: string;
  minLevel: number;
  count: number;
  hasMore: boolean;
  rows: LogRow[];
}

interface AppRow {
  app: string;
  host: string;
  kind: string;
  /** 기간 내 로그가 없는 서비스는 null (목록에는 남는다) */
  lastseen: string | null;
  cnt: number;
  errcnt: number;
}

interface KindRow { kind: string; apps: number; cnt: number; errcnt: number; }
interface AppsRes { hours: number; byKind: KindRow[]; apps: AppRow[]; }

interface StatRow { lvl: number; level: string; cnt: number; }
interface StatsRes {
  hours: number;
  byLevel: StatRow[];
  lastLog?: string | null;
  lastIngest?: string | null;
  total: number;
}

/**
 * 앱 분류. 서버(LogViewerEndpoints.KindExpr)와 같은 값이다.
 *  service  — systemd 상주 서비스
 *  batch    — cron 일별 배치
 *  backfill — 과거분 소급 적재(대부분 끝난 작업)
 *  other    — 수동 실행 등
 */
const KINDS = [
  { v: 'all', t: '전체' },
  { v: 'service', t: '서비스' },
  { v: 'batch', t: '배치' },
  { v: 'backfill', t: '백필' },
  { v: 'other', t: '기타' },
];

const kindLabel = (k: string): string => KINDS.find(x => x.v === k)?.t ?? k;

// 레벨별 색. 한국 주식 관례(상승=빨강)와 무관한 심각도 색이다.
const levelColor = (lvl: number): string => {
  if (lvl >= 21) return '#7f1d1d';      // FATAL
  if (lvl >= 17) return '#dc2626';      // ERROR
  if (lvl >= 13) return '#d97706';      // WARN
  if (lvl >= 9) return '#2563eb';       // INFO
  return colors.muted;
};

const LEVELS = [
  { v: 21, t: 'FATAL' },
  { v: 17, t: 'ERROR 이상' },
  { v: 13, t: 'WARN 이상' },
  { v: 9, t: 'INFO 이상' },
  { v: 1, t: '전체' },
];

const RANGES = [
  { v: 1, t: '최근 1시간' },
  { v: 6, t: '최근 6시간' },
  { v: 24, t: '최근 1일' },
  { v: 72, t: '최근 3일' },
  { v: 168, t: '최근 7일' },
  { v: 336, t: '최근 14일' },
  { v: 720, t: '최근 30일' },
  { v: 2160, t: '최근 90일' },
];

const fmtTime = (s: string): string => {
  const d = new Date(s);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

/** 적재가 멈췄는지 — 마지막 커서 갱신이 10분 넘게 없으면 의심. */
const ingestStale = (lastIngest?: string | null): boolean => {
  if (!lastIngest) return true;
  return Date.now() - new Date(lastIngest).getTime() > 10 * 60 * 1000;
};

const PAGE = 200;

const LogViewer: React.FC = () => {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [apps, setApps] = useState<AppRow[]>([]);
  const [byKind, setByKind] = useState<KindRow[]>([]);
  const [stats, setStats] = useState<StatsRes | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // 필터
  const [hours, setHours] = useState(24);
  const [minLevel, setMinLevel] = useState(13);
  const [kind, setKind] = useState('all');   // 분류: 전체/서비스/배치/백필/기타
  const [app, setApp] = useState('');        // 개별 앱 — 고르면 kind 보다 우선
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');

  const load = useCallback(async (append = false) => {
    setLoading(true);
    setErr(null);
    try {
      const axios = getMainAxios();
      const to = new Date();
      const from = new Date(to.getTime() - hours * 3600 * 1000);
      const res = await axios.get<SearchRes>('/api/v1/applogs', {
        params: {
          from: from.toISOString(),
          to: to.toISOString(),
          minLevel,
          kind: app ? undefined : kind,
          app: app || undefined,
          q: q || undefined,
          limit: PAGE,
          offset: append ? rows.length : 0,
        },
      });
      setRows(prev => (append ? [...prev, ...res.data.rows] : res.data.rows));
      setHasMore(res.data.hasMore);
      if (!append) setExpanded(null);
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? e?.message ?? '조회 실패');
    } finally {
      setLoading(false);
    }
  }, [hours, minLevel, kind, app, q, rows.length]);

  const loadMeta = useCallback(async () => {
    try {
      const axios = getMainAxios();
      const [a, s] = await Promise.all([
        // 앱 목록은 화면 기간과 같은 범위로 — 7일 고정이면 "30일 보기"에서
        // 목록에 없는 앱의 로그가 뜨는 모순이 생긴다.
        axios.get<AppsRes>('/api/v1/applogs/apps', { params: { hours } }),
        // 상단 요약도 현재 분류/앱 기준 — 전체 건수를 보여주면 목록과 숫자가 어긋난다.
        axios.get<StatsRes>('/api/v1/applogs/stats', {
          params: { hours, kind: app ? undefined : kind, app: app || undefined },
        }),
      ]);
      setApps(a.data.apps);
      setByKind(a.data.byKind);
      setStats(s.data);
    } catch {
      /* 메타 실패는 목록 조회를 막지 않는다 */
    }
  }, [hours, kind, app]);

  // 필터가 바뀌면 처음부터 다시 — rows.length 의존을 피하려고 load 를 직접 부르지 않는다
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const axios = getMainAxios();
        const to = new Date();
        const from = new Date(to.getTime() - hours * 3600 * 1000);
        const res = await axios.get<SearchRes>('/api/v1/applogs', {
          params: {
            from: from.toISOString(), to: to.toISOString(),
            minLevel,
            kind: app ? undefined : kind,   // 개별 앱이 분류보다 우선
            app: app || undefined,
            q: q || undefined,
            limit: PAGE, offset: 0,
          },
        });
        if (!alive) return;
        setRows(res.data.rows);
        setHasMore(res.data.hasMore);
        setExpanded(null);
      } catch (e: any) {
        if (alive) setErr(e?.response?.data?.message ?? e?.message ?? '조회 실패');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [hours, minLevel, kind, app, q]);

  useEffect(() => { void loadMeta(); }, [loadMeta]);

  /**
   * 개별 앱 드롭다운 — 선택된 분류에 속한 앱만, **분류별로 묶어서** 보여준다.
   *
   * optgroup 으로 나누는 이유: 서비스는 평소 로그가 적어(주말엔 1건) 건수순으로
   * 정렬하면 22개 목록 맨 아래로 밀린다. 정작 자주 찾는 것이 서비스인데도.
   */
  const appGroups = useMemo(() => {
    const byKindMap = new Map<string, { app: string; cnt: number; err: number }[]>();
    const seen = new Map<string, { kind: string; cnt: number; err: number }>();

    apps
      .filter(a => kind === 'all' || a.kind === kind)
      .forEach(a => {
        const cur = seen.get(a.app) ?? { kind: a.kind, cnt: 0, err: 0 };
        seen.set(a.app, {
          kind: a.kind,
          cnt: cur.cnt + Number(a.cnt),
          err: cur.err + Number(a.errcnt),
        });
      });

    seen.forEach((v, app) => {
      const list = byKindMap.get(v.kind) ?? [];
      list.push({ app, cnt: v.cnt, err: v.err });
      byKindMap.set(v.kind, list);
    });

    // 분류 순서는 KINDS 순(서비스 → 배치 → 백필 → 기타), 그룹 안에서는 건수순
    return KINDS
      .filter(k => k.v !== 'all')
      .map(k => ({
        kind: k.v,
        label: k.t,
        items: (byKindMap.get(k.v) ?? []).sort((x, y) => y.cnt - x.cnt),
      }))
      .filter(g => g.items.length > 0);
  }, [apps, kind]);

  /** 분류를 바꾸면 개별 앱 선택을 푼다 — 안 그러면 빈 목록이 된다. */
  const changeKind = useCallback((k: string) => {
    setKind(k);
    setApp('');
  }, []);

  const kindCount = useCallback(
    (k: string) => byKind.find(x => x.kind === k),
    [byKind],
  );
  const totalCnt = useMemo(() => byKind.reduce((s, x) => s + Number(x.cnt), 0), [byKind]);
  const totalErr = useMemo(() => byKind.reduce((s, x) => s + Number(x.errcnt), 0), [byKind]);

  const stale = ingestStale(stats?.lastIngest);

  return (
    <div style={page}>
      <div style={card}>
        <div style={titleRow}>
          <div>
            <div style={gridTitle}>LogViewer</div>
            <div style={gridHint}>
              서비스·배치 로그 (main.applogtsdb) — 적재: fdsloopsvc
            </div>
          </div>
          <button
            style={btnPrimary}
            onClick={() => { void load(false); void loadMeta(); }}
            disabled={loading}
          >
            {loading ? '조회 중…' : '새로고침'}
          </button>
        </div>

        {/* 요약 — 적재가 멈춘 것과 로그가 없는 것을 구분해 보여준다 */}
        {stats && (
          <div style={{
            display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center',
            fontSize: 12, color: colors.body, padding: '8px 0',
            borderTop: `1px solid ${colors.divider}`,
          }}>
            {stats.byLevel.map(s => (
              <span key={s.lvl}>
                <b style={{ color: levelColor(s.lvl) }}>{s.level}</b>{' '}
                {Number(s.cnt).toLocaleString()}건
              </span>
            ))}
            <span style={{ color: colors.muted }}>
              마지막 로그 {stats.lastLog ? fmtTime(stats.lastLog) : '—'}
            </span>
            <span style={{ color: stale ? '#dc2626' : colors.muted, fontWeight: stale ? 600 : 400 }}>
              적재 {stats.lastIngest ? fmtTime(stats.lastIngest) : '—'}
              {stale && ' ⚠ 10분 이상 멈춤'}
            </span>
          </div>
        )}

        {/* 분류 — 전체 / 서비스 / 배치 / 백필 / 기타 */}
        <div style={{
          display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
          padding: '8px 0', borderTop: `1px solid ${colors.divider}`,
        }}>
          <span style={toolbarLabel}>분류</span>
          {KINDS.map(k => {
            const on = kind === k.v && !app;
            const info = k.v === 'all'
              ? { cnt: totalCnt, errcnt: totalErr, apps: apps.length }
              : kindCount(k.v);
            const cnt = Number(info?.cnt ?? 0);
            const errcnt = Number(info?.errcnt ?? 0);
            return (
              <button
                key={k.v}
                onClick={() => changeKind(k.v)}
                title={`${cnt.toLocaleString()}건 · 오류 ${errcnt.toLocaleString()}건`}
                style={{
                  padding: '4px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                  border: `1px solid ${on ? colors.btnPrimary : colors.inputBorder}`,
                  background: on ? colors.btnPrimary : colors.cardBg,
                  color: on ? '#fff' : colors.body,
                  fontWeight: on ? 600 : 400,
                }}
              >
                {k.t}
                {cnt > 0 && (
                  <span style={{ opacity: on ? 0.85 : 0.6, marginLeft: 5 }}>
                    {cnt.toLocaleString()}
                  </span>
                )}
                {errcnt > 0 && (
                  <span style={{ color: on ? '#fecaca' : '#dc2626', marginLeft: 4 }}>
                    ●{errcnt.toLocaleString()}
                  </span>
                )}
              </button>
            );
          })}
          {app && (
            <span style={{ fontSize: 12, color: colors.muted }}>
              — 개별 <b style={{ color: colors.title }}>{app}</b> 선택됨
            </span>
          )}
        </div>

        {/* 필터 */}
        <div style={{
          display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
          padding: '8px 0', borderTop: `1px solid ${colors.divider}`,
        }}>
          <span style={toolbarLabel}>기간</span>
          <select style={selectSm} value={hours} onChange={e => setHours(Number(e.target.value))}>
            {RANGES.map(r => <option key={r.v} value={r.v}>{r.t}</option>)}
          </select>

          <span style={toolbarLabel}>레벨</span>
          <select style={selectSm} value={minLevel} onChange={e => setMinLevel(Number(e.target.value))}>
            {LEVELS.map(l => <option key={l.v} value={l.v}>{l.t}</option>)}
          </select>

          <span style={toolbarLabel}>개별</span>
          <select
            style={{ ...selectSm, minWidth: 200 }}
            value={app}
            onChange={e => setApp(e.target.value)}
          >
            <option value="">
              {kind === 'all' ? '전체 (분류 무관)' : `${kindLabel(kind)} 전체`}
            </option>
            {appGroups.map(g => (
              <optgroup key={g.kind} label={g.label}>
                {g.items.map(it => (
                  <option key={it.app} value={it.app}>
                    {it.app}
                    {it.cnt === 0
                      ? ' (로그 없음)'
                      : ` (${it.cnt.toLocaleString()}${it.err > 0 ? ` · 오류 ${it.err.toLocaleString()}` : ''})`}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          <input
            style={{ ...selectSm, width: 220 }}
            placeholder="메시지 검색 후 Enter"
            value={qInput}
            onChange={e => setQInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') setQ(qInput); }}
          />
          {q && (
            <button
              style={{ ...selectSm, cursor: 'pointer' }}
              onClick={() => { setQ(''); setQInput(''); }}
            >
              검색 해제
            </button>
          )}
        </div>

        {err && <div style={{ ...emptyMsg, color: '#dc2626' }}>{err}</div>}

        {!err && rows.length === 0 && !loading && (
          <div style={emptyMsg}>
            {stale
              ? '로그가 없다 — 적재(fdsloopsvc)가 멈춰 있을 수 있다.'
              : app
                // 서비스는 정상일 때 Warning 이상을 거의 안 남긴다.
                // "조용한 것" 과 "죽은 것" 을 섞어 말하지 않는다 — 적재는 위 요약이 보여준다.
                ? `${app} — 이 기간에 ${minLevel >= 17 ? 'ERROR' : 'WARN'} 이상 로그가 없다. (정상일 수 있다)`
                : '해당 조건의 로그가 없다.'}
          </div>
        )}

        {/* 목록 */}
        {rows.map((r, i) => {
          const open = expanded === i;
          return (
            <div key={`${r.t}-${i}`} style={listRow(levelColor(r.lvl))}>
              <div
                style={listRowHeader}
                onClick={() => setExpanded(open ? null : i)}
              >
                <span style={{ color: colors.muted, minWidth: 96, fontFamily: 'monospace' }}>
                  {fmtTime(r.t)}
                </span>
                <span style={{ color: levelColor(r.lvl), fontWeight: 600, minWidth: 48 }}>
                  {r.level}
                </span>
                <span style={{ color: colors.title, fontWeight: 600, minWidth: 110 }}>
                  {r.app}
                </span>
                <span style={{
                  color: colors.body, flex: 1, minWidth: 200,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {r.msg}
                </span>
                {r.ex && <span style={{ color: '#dc2626', fontSize: 11 }}>예외</span>}
              </div>

              {open && (
                <div style={listRowDetail}>
                  <div style={{
                    fontSize: 12, color: colors.body, whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all', marginBottom: 6,
                  }}>
                    {r.msg}
                  </div>
                  {r.ex && (
                    <pre style={{
                      fontSize: 11, color: '#7f1d1d', background: '#fef2f2',
                      padding: 8, borderRadius: 4, overflow: 'auto', maxHeight: 320,
                      margin: '6px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                    }}>
                      {r.ex}
                    </pre>
                  )}
                  <div style={{ fontSize: 11, color: colors.hint }}>
                    {r.kind && <>{kindLabel(r.kind)} · </>}host {r.host}
                    {r.tmpl && <> · 템플릿 <code>{r.tmpl}</code></>}
                    {r.src && <> · {r.src}</>}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {hasMore && (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <button style={btnPrimary} onClick={() => void load(true)} disabled={loading}>
              {loading ? '불러오는 중…' : `더 보기 (+${PAGE})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogViewer;
